import * as React from "react";
import { Check, X } from "lucide-react";

import { cn } from "@ando/ui";

import type { AdminSetPasswordPolicyRequest } from "@ando/core";

/**
 * PasswordStrengthMeter — 4-segment bar + requirements checklist.
 *
 * v1 reference: ``ando-frontend/app/admin-v2/components/pages/
 * organizations/components/PasswordStrengthMeter.tsx`` (58 LoC). v1 reads
 * the score from a project-local ``validation.ts::scorePasswordExample``
 * that is not exported under ``@ando/core``. v2 ships a self-contained
 * scorer aligned with the v1 thresholds (length × complexity × charset
 * diversity → 0–4) so admins can validate the active org policy.
 *
 * Score derivation:
 *   - +1 if length ≥ 8
 *   - +1 if length ≥ 12 AND ≥3 charset classes (upper / lower / digit /
 *     symbol)
 *   - +1 if length ≥ 16 AND 4 charset classes
 *   - +1 if length ≥ 20 AND not a common-leaked sequence
 * Cap at 4. Empty input renders the "neutral" state (0 segments lit).
 *
 * Pure presentational; the parent (``OrgSecurityTab``) owns the input
 * field and passes ``password`` down. Optionally accepts the current
 * org ``policy`` so the meter can show ✓/✗ next to each requirement.
 *
 * a11y: live region announces the textual label so screen readers
 * pick up the strength change without the visual diff.
 */

const SEGMENT_COLOR_BY_SCORE: Record<number, string> = {
  0: "bg-hairline",
  1: "bg-destructive/70",
  2: "bg-warning/70",
  3: "bg-accent/80",
  4: "bg-success/80",
};

const LABEL_BY_SCORE: Record<number, string> = {
  0: "Vuota",
  1: "Debole",
  2: "Media",
  3: "Forte",
  4: "Eccellente",
};

const LABEL_TONE: Record<number, string> = {
  0: "text-ink-3",
  1: "text-destructive",
  2: "text-warning-foreground",
  3: "text-accent",
  4: "text-success-foreground",
};

const COMMON_LEAKED = new Set([
  "password",
  "qwerty",
  "12345678",
  "letmein",
  "welcome1",
  "admin1234",
]);

function labelFor(score: 0 | 1 | 2 | 3 | 4): string {
  // ``LABEL_BY_SCORE`` is a complete Record<0..4, string> by construction,
  // but ``noUncheckedIndexedAccess`` still widens to ``string | undefined``.
  // Fall back to the empty string defensively — never observable since the
  // input domain is closed by the type system.
  return LABEL_BY_SCORE[score] ?? "";
}

function scorePassword(input: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  if (!input) return { score: 0, label: labelFor(0) };
  const len = input.length;
  const lower = /[a-z]/.test(input);
  const upper = /[A-Z]/.test(input);
  const digit = /\d/.test(input);
  const symbol = /[^A-Za-z0-9]/.test(input);
  const classes = [lower, upper, digit, symbol].filter(Boolean).length;
  let score = 0;
  if (len >= 8) score += 1;
  if (len >= 12 && classes >= 3) score += 1;
  if (len >= 16 && classes >= 4) score += 1;
  if (len >= 20 && !COMMON_LEAKED.has(input.toLowerCase())) score += 1;
  const capped = Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
  return { score: capped, label: labelFor(capped) };
}

interface RequirementRowProps {
  label: string;
  ok: boolean;
}

function RequirementRow({ label, ok }: RequirementRowProps): React.ReactElement {
  const Icon = ok ? Check : X;
  return (
    <li
      className={cn(
        "flex items-center gap-1.5 text-[11.5px]",
        ok ? "text-success-foreground" : "text-ink-3",
      )}
    >
      <Icon size={12} aria-hidden className={ok ? "text-success-foreground" : "text-ink-3"} />
      {label}
    </li>
  );
}

export interface PasswordStrengthMeterProps {
  /** The candidate password the admin is testing. */
  password: string;
  /** Optional active org policy — when present, requirements show ✓/✗. */
  policy?: AdminSetPasswordPolicyRequest | null;
}

export function PasswordStrengthMeter({
  password,
  policy,
}: PasswordStrengthMeterProps): React.ReactElement {
  const { score, label } = scorePassword(password);

  const checks = React.useMemo(() => {
    const lower = /[a-z]/.test(password);
    const upper = /[A-Z]/.test(password);
    const digit = /\d/.test(password);
    const symbol = /[^A-Za-z0-9]/.test(password);
    return { lower, upper, digit, symbol };
  }, [password]);

  return (
    <div className="space-y-2" aria-live="polite">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((seg) => (
          <div
            key={seg}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors duration-150",
              seg <= score ? SEGMENT_COLOR_BY_SCORE[score] : "bg-hairline",
            )}
            aria-hidden
          />
        ))}
      </div>
      <p className={cn("font-medium text-[11.5px]", LABEL_TONE[score])}>
        {password
          ? `Forza password: ${label}`
          : "Digita una password di esempio"}
      </p>
      {policy ? (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1">
          <RequirementRow
            label={`≥ ${policy.min_length} caratteri`}
            ok={password.length >= policy.min_length}
          />
          {policy.has_lowercase ? (
            <RequirementRow label="Minuscole (a-z)" ok={checks.lower} />
          ) : null}
          {policy.has_uppercase ? (
            <RequirementRow label="Maiuscole (A-Z)" ok={checks.upper} />
          ) : null}
          {policy.has_number ? (
            <RequirementRow label="Numeri (0-9)" ok={checks.digit} />
          ) : null}
          {policy.has_symbol ? (
            <RequirementRow label="Simboli (!@#$)" ok={checks.symbol} />
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
